import { useState, useEffect } from 'react';
import { Ticket, Plus, Paperclip, X, Camera, ImagePlus } from 'lucide-react';
import apiService from '../../services/apiService';
import { useToast } from '../ToastProvider';
import { useI18n } from '../../utils/i18n';
import Modal from '../ui/Modal';
import TicketCustomerField from '../TicketCustomerField';
import { CalenderMarkerHeaderSelect } from '../../utils/calenderMarkerUtils';

// #528 (Scheibe 2 aus #313): Dialog „Neues Ticket“ im Dashboard. Code
// unveraendert aus Dashboard.js uebernommen, Muster aus #312/#471: Den Zustand
// haelt der Hook, und das Dashboard ruft ihn auf. Die Komponente rendert nur.
//
// Warum der Hook nicht in den Dialog darf: Das Dashboard hat ein fruehes
// `return` fuer Laden und Fehler. Eine Komponente darunter wuerde dabei
// entfernt, und der Entwurf (Titel, Beschreibung, Kunde ...) waere weg. Heute
// uebersteht er Abbrechen, Schliessen und "Erneut versuchen";
// Dashboard.scheiben.test.js haelt das fest.
//
// Von aussen kommen nur zwei Dinge: das Neuladen der Ticketliste
// (manualRefreshTickets aus useAutoCache, steht im Dashboard weiter unten) und
// der Zaehler, an dem der Heute-Kalender seine Verknuepfungen neu liest.
// dateiVorbereitung (optional): wird vor dem Hochladen auf jede Datei
// angewendet. Die Mobilansicht verkleinert damit Handyfotos; ohne Angabe
// gehen die Dateien unveraendert hoch wie bisher.
export function useCreateTicket({ manualRefreshTickets, setEventRefreshKey, dateiVorbereitung = null }) {
  const { pushToast } = useToast();
  const { t } = useI18n();

  const [showCreateTicketForm, setShowCreateTicketForm] = useState(false);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'normal',
    category: '',
    tags: [],
    calenderMarker: null,
    assignedTo: '',
    customerId: null,
    contactId: null
  });
  // Files dropped globally to create a new ticket
  const [pendingCreateTicketFiles, setPendingCreateTicketFiles] = useState([]);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);

  // Load available assignee emails for the create-ticket modal
  const loadAvailableEmails = async () => {
    try {
      const emailsResponse = await apiService.getAvailableEmails();
      setAvailableEmails(emailsResponse.emails || []);
    } catch (error) {
      console.error('Error loading available emails:', error);
    }
  };

  // Preload on mount so dropdown is ready when opening the modal
  useEffect(() => {
    loadAvailableEmails();
  }, []);

  // Also refresh when the create-ticket modal is opened
  useEffect(() => {
    if (showCreateTicketForm) {
      loadAvailableEmails();
    }
  }, [showCreateTicketForm]);

  // Listen for global event to open Create Ticket with pending attachments
  useEffect(() => {
    const openCreateWithFiles = async () => {
      try {
        const files = Array.from(window.__pendingCreateTicketAttachments || []);
        if (files && files.length > 0) {
          setPendingCreateTicketFiles(files);

          // Try to prefill from first email-like file (.msg)
          try {
            const emailLike = files.find(f => /\.msg$/i.test(f.name) || f.type === 'application/vnd.ms-outlook' || f.type === 'application/vnd.ms-office');
            if (emailLike) {
              const parsed = await apiService.parseEmailFile(emailLike);
              if (parsed && parsed.success) {
                const title = parsed.subject || 'Neues Ticket';
                const description = parsed.bodyPreview || parsed.bodyText || '';
                setNewTicket(prev => ({
                  ...prev,
                  title: title || prev.title || 'Neues Ticket',
                  description: description,
                  category: 'E-Mail'
                }));
              } else {
                // Fallback: use filename without extension
                const base = (emailLike.name || '').replace(/\.[^.]+$/i, '') || 'Neues Ticket';
                setNewTicket(prev => ({ ...prev, title: base, category: 'E-Mail' }));
              }
            } else {
              // No email file - optional: set title to first filename base
              const first = files[0];
              if (first && first.name) {
                const base = first.name.replace(/\.[^.]+$/i, '');
                setNewTicket(prev => ({ ...prev, title: base || prev.title || 'Neues Ticket' }));
              }
            }
          } catch (e) {
            // ignore prefill errors
          }

          setShowCreateTicketForm(true);
        }
      } catch (_) {}
    };

    window.addEventListener('openCreateTicketWithPendingAttachments', openCreateWithFiles);
    // Also check once on mount in case event fired before listener attached
    setTimeout(openCreateWithFiles, 0);
    return () => window.removeEventListener('openCreateTicketWithPendingAttachments', openCreateWithFiles);
  }, []);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (isCreatingTicket) return;
    
    if (!newTicket.title.trim()) {
      pushToast({ message: t('dashboard.enterTitle', 'Bitte geben Sie einen Titel ein.'), type: 'error' });
      return;
    }

    try {
      setIsCreatingTicket(true);
      const response = await apiService.createTicket(newTicket);
      const createdTicket = response?.ticket || response;
      setShowCreateTicketForm(false);
      setNewTicket({
        title: '',
        description: '',
        priority: 'normal',
        category: '',
        tags: [],
        calenderMarker: null,
        assignedTo: '',
        customerId: null,
        contactId: null
      });
      // Upload pending attachments if present
      try {
        if (createdTicket && createdTicket.id && Array.isArray(pendingCreateTicketFiles) && pendingCreateTicketFiles.length > 0) {
          const dateien = dateiVorbereitung
            ? await Promise.all(pendingCreateTicketFiles.map(dateiVorbereitung))
            : pendingCreateTicketFiles;
          await apiService.uploadTicketAttachments(createdTicket.id, dateien, '');
        }
      } catch (uploadErr) {
        console.error('Error uploading attachments after ticket creation:', uploadErr);
        pushToast({ message: t('dashboard.attachmentUploadError', 'Ticket wurde erstellt, aber Anhänge konnten nicht hochgeladen werden.'), type: 'error' });
      } finally {
        try { window.__pendingCreateTicketAttachments = []; } catch (_) {}
        setPendingCreateTicketFiles([]);
      }
      // Sofortige Aktualisierung der Tickets (wie nach 60s)
      await manualRefreshTickets();
      // Trigger CompactEventList to refresh linked items
      setEventRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error creating ticket:', error);
      pushToast({ message: t('dashboard.createTicketError', 'Fehler beim Erstellen des Tickets.'), type: 'error' });
    } finally {
      setIsCreatingTicket(false);
    }
  };

  return {
    showCreateTicketForm, setShowCreateTicketForm, availableEmails,
    newTicket, setNewTicket, pendingCreateTicketFiles, setPendingCreateTicketFiles,
    isCreatingTicket, handleCreateTicket,
  };
}

// kameraKnopf: Die Mobilansicht zeigt wie in ihrer Ticket-Detailansicht zwei
// Knoepfe, "Foto" (Kamera direkt) und "Dateien". Am Desktop bleibt der
// eine Knopf "Datei/Foto hinzufuegen".
export default function CreateTicketModal({ anlegen, kameraKnopf = false }) {
  const { t } = useI18n();
  const {
    showCreateTicketForm, setShowCreateTicketForm, availableEmails,
    newTicket, setNewTicket, pendingCreateTicketFiles, setPendingCreateTicketFiles,
    isCreatingTicket, handleCreateTicket,
  } = anlegen;

  return (
    <>
      {/* CREATE TICKET MODAL */}
      <Modal
        open={showCreateTicketForm}
        onClose={() => { setShowCreateTicketForm(false); setPendingCreateTicketFiles([]); try { window.__pendingCreateTicketAttachments = []; } catch (_) {} }}
        title={<>
          <Ticket size={24} />
          <h3>{t('dashboard.createTicketHeader', 'Neues Ticket erstellen')}</h3>
          <CalenderMarkerHeaderSelect value={newTicket.calenderMarker} onChange={(value) => setNewTicket({...newTicket, calenderMarker: value})} />
          <span title={t('dashboard.dragDropHint', 'Dateien per Drag & Drop hierher ziehen')} style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--color-text-muted)' }}>
            <Paperclip size={18} />
            {pendingCreateTicketFiles.length > 0 && <span style={{ fontSize: '0.85rem' }}>{pendingCreateTicketFiles.length} {t('dashboard.attachmentsSuffix', 'Anhänge')}</span>}
          </span>
        </>}
        ariaLabel={t('dashboard.createTicketHeader', 'Neues Ticket erstellen')}
        footer={<>
          <button type="button" onClick={() => setShowCreateTicketForm(false)} className="btn btn-secondary">{t('dashboard.cancel', 'Abbrechen')}</button>
          <button type="submit" form="dashboard-create-ticket-form" className="btn btn-primary" disabled={isCreatingTicket}><Plus size={16} />{isCreatingTicket ? t('common.creating', 'Erstelle...') : t('dashboard.createTicket', 'Ticket erstellen')}</button>
        </>}
      >
            
            <form id="dashboard-create-ticket-form" onSubmit={handleCreateTicket} className="create-ticket-form">
              <div className="form-body">
                {/* #104: Kunde (halbbreit) über dem Titel */}
                <TicketCustomerField
                  value={newTicket.customerId}
                  onChange={(customerId) => setNewTicket(prev => ({...prev, customerId}))}
                  contactValue={newTicket.contactId}
                  onContactChange={(contactId) => setNewTicket(prev => ({...prev, contactId}))}
                  inputId="ticket-customer"
                />
                <div className="form-group">
                  <label htmlFor="ticket-title">{t('dashboard.field.title', 'Titel')} <span className="required">*</span></label>
                  <input
                    id="ticket-title"
                    type="text"
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                    required
                    placeholder={t('dashboard.placeholder.title', 'Ticket-Titel eingeben...')}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="ticket-description">{t('dashboard.field.description', 'Beschreibung')}</label>
                  <textarea
                    id="ticket-description"
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                    placeholder={t('dashboard.placeholder.description', 'Ticket-Beschreibung eingeben...')}
                    rows="4"
                    className="form-textarea"
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="ticket-priority">{t('dashboard.field.priority', 'Priorität')}</label>
                    <select
                      id="ticket-priority"
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                      className="form-select"
                    >
                      <option value="niedrig">Niedrig</option>
                      <option value="normal">Normal</option>
                      <option value="hoch">Hoch</option>
                      <option value="kritisch">Kritisch</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="ticket-assigned">{t('dashboard.field.assignedTo', 'Zugewiesen an')}</label>
                    <select
                      id="ticket-assigned"
                      value={newTicket.assignedTo}
                      onChange={(e) => setNewTicket({...newTicket, assignedTo: e.target.value})}
                      className="form-select"
                    >
                      <option value="">Nicht zugewiesen</option>
                      {availableEmails.map(email => (
                        <option key={email} value={email}>{email}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* W6 (#16 A2.4/F4): Anhänge auch ohne Drag & Drop anfügbar —
                    auf dem Handy öffnet der Datei-Dialog Kamera/Galerie direkt. */}
                <div className="form-group">
                  <label htmlFor="ticket-create-files">{t('dashboard.field.attachments', 'Anhänge')}</label>
                  <input
                    id="ticket-create-files"
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) setPendingCreateTicketFiles(prev => [...prev, ...files]);
                      try { e.target.value = ''; } catch (_) {}
                    }}
                  />
                  {kameraKnopf ? (
                    <>
                      <input
                        id="ticket-create-camera"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length > 0) setPendingCreateTicketFiles(prev => [...prev, ...files]);
                          try { e.target.value = ''; } catch (_) {}
                        }}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          className="btn btn-secondary mobil-touch"
                          onClick={() => { try { document.getElementById('ticket-create-camera').click(); } catch (_) {} }}
                          style={{ flex: 1, gap: 6 }}
                        >
                          <Camera size={18} />
                          {t('mobile.takePhoto', 'Foto')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary mobil-touch"
                          onClick={() => { try { document.getElementById('ticket-create-files').click(); } catch (_) {} }}
                          style={{ flex: 1, gap: 6 }}
                        >
                          <ImagePlus size={18} />
                          {t('mobile.chooseFiles', 'Dateien')}
                        </button>
                      </div>
                    </>
                  ) : (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { try { document.getElementById('ticket-create-files').click(); } catch (_) {} }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Paperclip size={16} />
                    {t('dashboard.addAttachment', 'Datei/Foto hinzufügen')}
                  </button>
                  )}
                  {pendingCreateTicketFiles.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                      {pendingCreateTicketFiles.map((file, idx) => (
                        <div key={`${file.name}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                          <Paperclip size={14} />
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                          <button
                            type="button"
                            onClick={() => setPendingCreateTicketFiles(prev => prev.filter((_, i) => i !== idx))}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}
                            title={t('dashboard.removeAttachment', 'Anhang entfernen')} aria-label={t('dashboard.removeAttachment', 'Anhang entfernen')}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

        </form>
      </Modal>
    </>
  );
}
